/*
 * Free-shipping offer — email capture, then the real discount code.
 *
 * This needs NO custom backend. Both halves use mechanisms Shopify already
 * provides:
 *   - capture  → the section's own {% form 'customer' %}, posted with fetch,
 *                exactly like assets/email-offer.js. Creates the customer with
 *                the configured tags, so the list is real.
 *   - redeem   → /discount/<CODE>, Shopify's native discount URL. It validates
 *                the code server-side, attaches it to the cart session and
 *                redirects. A front-end "is this valid?" check is not possible
 *                and was never real; this hands the job to the only thing that
 *                can actually answer it.
 *
 * The code itself is a section setting (data-discount-code), so it is changed
 * in the theme editor rather than here.
 *
 * Eligibility — minimum spend, per-customer limits, expiry, stackability — is
 * owned by the discount in the Shopify admin, which is where it belongs. The
 * front end deliberately encodes none of it.
 */
(function () {
  'use strict';

  /* ============================================================
   * INTERFACE LAYER — override any of these to change behaviour without
   * touching markup or the custom elements below.
   * ============================================================ */
  var cfg = (window.PFFreeShip = window.PFFreeShip || {});

  /**
   * requestCode(email, form) -> Promise<{ code: string }>
   * Posts the section's real customer form (so the email is actually stored),
   * then resolves the configured discount code for display.
   */
  cfg.requestCode =
    cfg.requestCode ||
    function (email, form, code) {
      if (!email) return Promise.reject(new Error('missing-email'));
      if (!form) return Promise.resolve({ code: code });

      return fetch(form.action, { method: 'POST', body: new FormData(form) }).then(function (response) {
        // Shopify bounces suspected spam to /challenge. Send the visitor there
        // rather than pretending the signup succeeded.
        if (response.url && response.url.indexOf('/challenge') !== -1) {
          form.submit();
          return new Promise(function () {});
        }
        if (!response.ok) throw new Error('signup-failed');
        return { code: code };
      });
    };

  /**
   * applyCode(code) -> navigates to Shopify's discount URL.
   * Shopify validates and attaches it; an invalid code simply does not apply,
   * and the cart says so. Nothing to fake here.
   */
  cfg.applyCode =
    cfg.applyCode ||
    function (code, redirectTo) {
      var target = '/discount/' + encodeURIComponent(code);
      if (redirectTo) target += '?redirect=' + encodeURIComponent(redirectTo);
      window.location.assign(target);
    };

  /* Shared, dependency-free email sanity check (format only; not deliverability). */
  function looksLikeEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());
  }

  /* ============================================================
   * UI COMPONENT — email capture -> code display
   * ============================================================ */
  class FreeShipSignup extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-free-ship-form]');
      this.email = this.querySelector('[data-free-ship-email]');
      this.submit = this.querySelector('[data-free-ship-submit]');
      this.status = this.querySelector('[data-free-ship-status]');
      this.success = this.querySelector('[data-free-ship-success]');
      if (!this.form) return;

      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    setStatus(message, tone) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.dataset.tone = tone || '';
    }

    onSubmit(event) {
      event.preventDefault();
      var value = this.email ? this.email.value : '';
      if (!looksLikeEmail(value)) {
        this.email.setAttribute('aria-invalid', 'true');
        this.setStatus('Enter a valid email address.', 'error');
        this.email.focus();
        return;
      }
      this.email.removeAttribute('aria-invalid');
      this.submit.disabled = true;
      this.submit.setAttribute('aria-busy', 'true');
      this.setStatus('Sending your code…', 'pending');

      cfg.requestCode(value.trim(), this.form)
        .then(() => {
          this.submit.removeAttribute('aria-busy');
          this.renderSuccess();
        })
        .catch(() => {
          this.submit.disabled = false;
          this.submit.removeAttribute('aria-busy');
          this.setStatus('Something went wrong. Please try again.', 'error');
        });
    }

    renderSuccess() {
      this.setStatus('', '');
      if (this.form) this.form.hidden = true;
      if (this.success) {
        this.success.hidden = false;
        this.success.setAttribute('tabindex', '-1');
        this.success.focus();
      }
    }

  }

  /* ============================================================
   * UI COMPONENT — code entry (redeem) in the cart / pre-checkout flow
   * ============================================================ */
  class FreeShipRedeem extends HTMLElement {
    connectedCallback() {
      // No <form> here — this lives inside the cart's POST form, and nesting
      // forms is invalid. Apply on button click or Enter in the field instead.
      this.input = this.querySelector('[data-free-ship-code-input]');
      this.apply = this.querySelector('[data-free-ship-apply]');
      this.status = this.querySelector('[data-free-ship-redeem-status]');
      if (!this.apply || !this.input) return;
      this.apply.addEventListener('click', this.onApply.bind(this));
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.onApply();
        }
      });
    }

    setStatus(message, tone) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.dataset.tone = tone || '';
    }

    onApply() {
      var code = this.input ? this.input.value.trim() : '';
      if (!code) {
        this.input.setAttribute('aria-invalid', 'true');
        this.setStatus('Enter a code.', 'error');
        this.input.focus();
        return;
      }
      this.input.removeAttribute('aria-invalid');
      this.apply.disabled = true;
      this.apply.setAttribute('aria-busy', 'true');
      this.setStatus('Applying…', 'pending');

      // Hand off to Shopify. /discount/<CODE> is the only thing that can
      // actually validate a code and attach it to the cart — the previous
      // front-end "valid?" check confirmed nothing and applied nothing, so a
      // wrong code still reported success and then failed at checkout.
      cfg.applyCode(code, '/cart');
    }
  }

  if (!window.customElements.get('free-ship-signup')) {
    window.customElements.define('free-ship-signup', FreeShipSignup);
  }
  if (!window.customElements.get('free-ship-redeem')) {
    window.customElements.define('free-ship-redeem', FreeShipRedeem);
  }
})();
